import { useToast } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import FormField from '../../../components/fields/FormField';
import FormModal from '../../../components/FormModal';
import FormSection from '../../../components/form-section';
import FormStack from '../../../components/form-stack';
import { translate } from '../../../i18n';
import {
  CreateCreditPackInput,
  useCreateCreditPack,
} from '../hooks/useCreateCreditPack';
import { validateCreditPackForm } from '../utils/credit-pack.utils';

const defaultValues: CreateCreditPackInput = {
  name: '',
  priceInCents: 0,
  credits: 0,
  sortOrder: 0,
};

interface CreateCreditPackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CreateCreditPackModal({
  isOpen,
  onClose,
}: CreateCreditPackModalProps) {
  const { mutateAsync: createPack, isLoading } = useCreateCreditPack();
  const [formData, setFormData] = useState<CreateCreditPackInput>(defaultValues);
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      setFormData(defaultValues);
      setErrors({});
    }
  }, [isOpen]);

  const handleInputChange = (field: keyof CreateCreditPackInput, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors = validateCreditPackForm(formData, {
      nameRequired: translate('credit_pack.name_required'),
      priceRequired: translate('credit_pack.price_required'),
      creditsRequired: translate('credit_pack.credits_required'),
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      await createPack(formData);
      toast({
        title: translate('credit_pack.created_successfully'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
    } catch (error: any) {
      toast({
        title: translate('credit_pack.create_error'),
        description: error.message || '',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={translate('credit_pack.create_pack')}
      onSubmit={handleSubmit}
      submitLabel={translate('credit_pack.create')}
      cancelLabel={translate('common.cancel')}
      isSubmitting={isLoading}
      size="lg"
    >
      <FormStack density="section">
        <FormField
          type="text"
          label={translate('credit_pack.name')}
          value={formData.name}
          onChange={(val) => handleInputChange('name', val)}
          placeholder={translate('credit_pack.name_placeholder')}
          error={errors.name}
        />

        <FormSection label={translate('credit_pack.pricing') || 'Pricing'} columns={{ base: 1, md: 2 }}>
          <FormField
            type="number"
            label={translate('credit_pack.price')}
            value={formData.priceInCents / 100 || 0}
            onChange={(val) => handleInputChange('priceInCents', Math.round((val || 0) * 100))}
            min={0}
            step={0.5}
            error={errors.priceInCents}
            numberInputProps={{ precision: 2 }}
          />

          <FormField
            type="number"
            label={translate('credit_pack.credits')}
            value={formData.credits || 0}
            onChange={(val) => handleInputChange('credits', val || 0)}
            min={0.01}
            step={0.5}
            error={errors.credits}
            numberInputProps={{ precision: 2 }}
          />
        </FormSection>

        <FormSection label={translate('credit_pack.display') || 'Display'}>
          <FormField
            type="number"
            label={translate('credit_pack.sort_order')}
            value={formData.sortOrder ?? 0}
            onChange={(val) => handleInputChange('sortOrder', val || 0)}
            min={0}
          />
        </FormSection>
      </FormStack>
    </FormModal>
  );
}
